<template>
  <!-- data-app="true" so Vuetify's overlay system (v-select menu, v-dialog
       etc.) finds an anchor when this page is rendered outside v-app. -->
  <div class="daemon-page" data-app="true">
    <v-container class="daemon-shell">
      <header class="daemon-header">
        <div>
          <img
            :src="logoUrl"
            alt="Phasi Charoen"
            class="daemon-brand"
            decoding="async"
          />
          <div class="daemon-kicker">
            <v-icon size="17" color="primary">mdi-database-sync</v-icon>
            <span>Etax FoxPro Daemon</span>
            <span class="daemon-kicker__dot" aria-hidden="true"></span>
            <span class="daemon-kicker__step">ขั้นที่ 1 จาก 2</span>
          </div>
          <h1>เชื่อม CLI กับบัญชี</h1>
          <p>ยืนยันสิทธิ์ admin แล้วส่ง custom token กลับไปยัง CLI บนเครื่องนี้ — บริษัทที่จะผูก daemon เลือกในขั้นถัดไป (ผูกได้หลายบริษัท)</p>
        </div>

        <div v-if="currentUserEmail" class="daemon-account" aria-label="Current account">
          <span class="daemon-account__dot" />
          <span class="daemon-account__email">{{ currentUserEmail }}</span>
          <v-btn
            icon
            x-small
            class="tw-ml-2"
            :loading="loggingOut"
            :title="'ออกจากระบบ'"
            @click="logout"
          >
            <v-icon x-small>mdi-logout</v-icon>
          </v-btn>
        </div>
      </header>

      <div class="daemon-layout">
        <aside class="daemon-rail" aria-label="Daemon setup progress">
          <div class="daemon-rail__title">ขั้นตอนการเชื่อมต่อ</div>

          <div class="daemon-step daemon-step--active">
            <div class="daemon-step__marker">1</div>
            <div>
              <div class="daemon-step__label">ส่ง token</div>
              <div class="daemon-step__meta">ยืนยันสิทธิ์ admin แล้วส่ง token ให้ CLI</div>
            </div>
          </div>

          <div class="daemon-step">
            <div class="daemon-step__marker">2</div>
            <div>
              <div class="daemon-step__label">จับคู่บริษัท</div>
              <div class="daemon-step__meta">เลือกบริษัท (หลายบริษัทได้) และ basePath</div>
            </div>
          </div>

          <div class="daemon-security-note">
            <v-icon size="18" color="success">mdi-shield-lock-outline</v-icon>
            <span>Token ถูกส่งไปยัง listener บนเครื่องคุณโดยตรง</span>
          </div>
        </aside>

        <v-card class="daemon-panel" elevation="0">
          <div class="daemon-panel__header">
            <div class="daemon-panel__icon">
              <v-icon size="24" color="primary">mdi-key-variant</v-icon>
            </div>
            <div>
              <h2>ส่ง token ให้ CLI</h2>
              <p>CLI กำลังรอรับ custom token จากเบราว์เซอร์ผ่าน local callback</p>
            </div>
          </div>

          <v-card-text class="daemon-panel__body">
            <transition name="daemon-fade">
              <v-alert
                v-if="error"
                type="error"
                outlined
                dense
                class="daemon-alert"
                role="alert"
              >
                <div class="daemon-alert__body">
                  <span>{{ error }}</span>
                  <v-btn
                    text
                    x-small
                    color="error"
                    aria-label="ปิดข้อความ"
                    @click="error = null"
                  >
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-alert>
            </transition>

            <transition name="daemon-fade">
              <div
                v-if="success"
                class="daemon-state daemon-state--success"
                role="status"
                aria-live="polite"
              >
                <div class="daemon-state__icon daemon-state__icon--pulse">
                  <v-icon size="28" color="success">mdi-check</v-icon>
                </div>
                <div>
                  <h3>ส่ง token สำเร็จ</h3>
                  <p>กลับไปที่ CLI ได้เลย ระบบจะเปิดหน้าจับคู่บริษัทอัตโนมัติ</p>
                  <div class="daemon-state__hint">
                    <v-icon size="14" color="success">mdi-arrow-right</v-icon>
                    <span>ขั้นถัดไป: จับคู่ daemon กับบริษัท</span>
                  </div>
                </div>
              </div>
            </transition>

            <template v-if="!success">
              <div class="daemon-field-block">
                <div class="daemon-section-label">
                  <v-icon small color="primary">mdi-domain</v-icon>
                  <span>เลือกบริษัทเพื่อยืนยันสิทธิ์</span>
                  <small>ใช้ตรวจสิทธิ์ admin เท่านั้น</small>
                </div>

                <v-select
                  v-model="selectedCompanyId"
                  :items="companyOptions"
                  outlined
                  hide-details
                  prepend-inner-icon="mdi-domain"
                  label="Company"
                  :loading="companiesLoading"
                  :disabled="submitting"
                  class="daemon-input"
                />

                <p class="daemon-link-hint">
                  <v-icon size="14" color="grey darken-1">mdi-information-outline</v-icon>
                  ใช้เพื่อยืนยันว่าคุณเป็น admin เท่านั้น — บริษัทที่ daemon จะผูกจริงเลือกในขั้นถัดไป และผูกได้หลายบริษัทพร้อมกัน
                </p>
              </div>

              <div
                v-if="!companiesLoading && adminCount === 0"
                class="daemon-state"
              >
                <div class="daemon-state__icon daemon-state__icon--muted">
                  <v-icon size="24" color="grey darken-1">mdi-account-off-outline</v-icon>
                </div>
                <div>
                  <h3>ไม่พบบริษัทที่คุณเป็น admin</h3>
                  <p>ตรวจว่าล็อกอินด้วยบัญชี admin หรือกด <strong>ออกจากระบบ</strong> แล้วล็อกอินใหม่</p>
                </div>
              </div>
              <div
                v-else-if="!companiesLoading && companyOptions.length === 0"
                class="daemon-state daemon-state--warning"
              >
                <div class="daemon-state__icon daemon-state__icon--warning">
                  <v-icon size="24" color="amber darken-2">mdi-toggle-switch-off-outline</v-icon>
                </div>
                <div>
                  <h3>ยังไม่มีบริษัทที่เปิด AP Automation</h3>
                  <p>
                    คุณเป็น admin <strong>{{ adminCount }}</strong> บริษัท แต่ยังไม่มีบริษัทไหนเปิดสิทธิ์
                    ติดต่อ support เพื่อเปิด AP Automation
                  </p>
                </div>
              </div>

              <div class="daemon-endpoint">
                <div>
                  <span>Local callback</span>
                  <code>127.0.0.1:{{ port || '—' }}/callback</code>
                </div>
                <v-icon size="18" :color="port ? 'success' : 'error'">
                  {{ port ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
                </v-icon>
              </div>

              <v-alert v-if="!port" type="error" outlined dense class="daemon-alert">
                ไม่พบ <code>?port</code> ใน URL หรือ port ไม่ถูกต้อง กรุณาเปิดลิงก์ผ่าน CLI โดยตรง
              </v-alert>

              <v-btn
                block
                large
                class="daemon-cta"
                :loading="submitting"
                :disabled="!canSubmit"
                @click="submit"
              >
                <v-icon left>mdi-send-outline</v-icon>
                ส่ง token ให้ CLI
              </v-btn>
            </template>
          </v-card-text>
        </v-card>
      </div>
    </v-container>
  </div>
</template>

<script>
import firebase from 'firebase/app';
import axios from 'axios';
import fc from '@/firebaseConfig';
import logoUrl from '@/assets/logo_phasi_charoen.svg';

export default {
  name: 'DaemonLink',
  data() {
    return {
      logoUrl,
      companies: [],
      companiesLoading: false,
      adminCount: 0,        // CompaniesUsers admin rows before the AP-Automation filter
      selectedCompanyId: null,
      submitting: false,
      loggingOut: false,
      error: null,
      success: false,
    };
  },
  computed: {
    port() {
      const p = this.$route.query.port;
      const n = Number(p);
      return Number.isInteger(n) && n > 0 && n <= 65535 ? n : null;
    },
    currentUid() {
      const u = firebase.auth().currentUser;
      return u ? u.uid : null;
    },
    currentUserEmail() {
      const u = firebase.auth().currentUser;
      return u ? (u.email || u.uid) : null;
    },
    companyOptions() {
      return this.companies.map(c => ({
        text: this._companyLabel(c),
        value: c.companyID,
      }));
    },
    canSubmit() {
      return Boolean(this.selectedCompanyId && this.port);
    },
  },
  mounted() {
    if (!this.currentUid) {
      const redirect = encodeURIComponent(this.$route.fullPath);
      this.$router.replace(`/login?redirect=${redirect}`);
      return;
    }
    this.loadCompanies();
  },
  methods: {
    async loadCompanies() {
      this.companiesLoading = true;
      this.adminCount = 0;
      try {
        const snap = await fc.db
          .collection('CompaniesUsers')
          .where('userID', '==', this.currentUid)
          .where('isAdmin', '==', true)
          .get();
        const candidates = [];
        snap.forEach((doc) => {
          const d = doc.data();
          if (d && d.companyID) candidates.push(d);
        });
        this.adminCount = candidates.length;

        // Only show companies that have AP Automation enabled.
        const checks = await Promise.all(candidates.map(async (c) => {
          try {
            const ps = await fc.companies
              .doc(c.companyID)
              .collection('ProductList')
              .doc('PSC')
              .get();
            const data = ps.exists ? (ps.data() || {}) : {};
            return data.isApAutomate === true ? c : null;
          } catch (e) {
            // Read failure skips only this company.
            return null;
          }
        }));
        this.companies = checks.filter(Boolean);
      } catch (err) {
        this.error = `โหลดรายการบริษัทไม่สำเร็จ: ${err.message}`;
      } finally {
        this.companiesLoading = false;
      }
    },
    async logout() {
      this.loggingOut = true;
      try {
        await firebase.auth().signOut();
        const redirect = encodeURIComponent(this.$route.fullPath);
        this.$router.replace(`/login?redirect=${redirect}`);
      } catch (err) {
        this.error = `ออกจากระบบไม่สำเร็จ: ${err.message}`;
        this.loggingOut = false;
      }
    },
    _companyLabel(c) {
      const co = c.company || {};
      // Prefer Thai name; fall back to English; last resort the companyID.
      return co.nameTH || co.nameEN || c.companyID;
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.error = null;
      try {
        // 1. Mint a custom token via Cloud Function.
        const idToken = await firebase.auth().currentUser.getIdToken(false);
        const res = await axios.post(
          `${fc.cloudFunctionUrl}apiGenerateDaemonToken`,
          { companyId: this.selectedCompanyId },
          { headers: { Authorization: `JWT ${idToken}`, 'Content-Type': 'application/json' } },
        );
        if (!res.data || !res.data.success || !res.data.customToken) {
          throw new Error((res.data && res.data.error) || 'ไม่ได้รับ custom token');
        }
        const customToken = res.data.customToken;

        // 2. Forward the token to the local CLI HTTP server.
        await axios.post(
          `http://127.0.0.1:${this.port}/callback`,
          { token: customToken },
          { headers: { 'Content-Type': 'application/json' } },
        );

        this.success = true;
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.error)
          || err.message
          || 'ไม่ทราบสาเหตุ';
        this.error = `ส่ง token ไม่สำเร็จ: ${msg}`;
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.daemon-page {
  min-height: 100vh;
  background:
    radial-gradient(1200px 400px at 50% -160px, #e0eaff 0%, transparent 70%),
    #f6f8fb;
  color: #0f172a;
  font-family: "Noto Sans Thai", "Noto Sans", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.daemon-page :deep(.v-btn:focus-visible),
.daemon-page :deep(.v-input--is-focused .v-input__slot) {
  outline: 2px solid #1d4ed8;
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .daemon-page *,
  .daemon-page *::before,
  .daemon-page *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

.daemon-shell {
  max-width: 1040px;
  padding: 40px 20px 48px;
}

.daemon-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}

.daemon-brand {
  display: block;
  height: 32px;
  width: auto;
  margin-bottom: 14px;
  user-select: none;
}

.daemon-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
}

.daemon-kicker__dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #94a3b8;
}

.daemon-kicker__step {
  padding: 2px 8px;
  background: #eff6ff;
  border-radius: 999px;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.daemon-header h1 {
  margin: 8px 0 6px;
  color: #0f172a;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0;
}

.daemon-header p {
  max-width: 620px;
  margin: 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.6;
}

.daemon-account {
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  max-width: 340px;
  padding: 6px 8px 6px 12px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
  font-size: 13px;
}

.daemon-account__dot {
  width: 8px;
  height: 8px;
  margin-right: 9px;
  border-radius: 50%;
  background: #16a34a;
}

.daemon-account__email {
  overflow: hidden;
  color: #0f172a;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.daemon-layout {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.daemon-rail,
.daemon-panel {
  background: #fff !important;
  border: 1px solid #dbe3ee;
  border-radius: 8px !important;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08) !important;
}

.daemon-rail {
  position: sticky;
  top: 24px;
  padding: 18px;
}

.daemon-rail__title {
  margin-bottom: 12px;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}

.daemon-step {
  position: relative;
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 12px;
  padding: 12px 0;
}

.daemon-step + .daemon-step::before {
  content: '';
  position: absolute;
  top: -12px;
  left: 15px;
  height: 24px;
  border-left: 2px solid #cbd5e1;
}

.daemon-step--done + .daemon-step::before {
  border-left-color: #16a34a;
}

.daemon-step__marker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.daemon-step--active .daemon-step__marker {
  background: #1d4ed8;
  border-color: #1d4ed8;
  box-shadow: 0 0 0 4px rgba(29, 78, 216, 0.14);
  color: #fff;
}

.daemon-step--done .daemon-step__marker {
  background: #16a34a;
  border-color: #16a34a;
  color: #fff;
}

.daemon-step__label {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
}

.daemon-step__meta {
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.daemon-security-note {
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  color: #475569;
  font-size: 12px;
  line-height: 1.5;
}

.daemon-panel {
  overflow: hidden;
}

.daemon-panel__header {
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 14px;
  align-items: center;
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
}

.daemon-panel__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #eff6ff;
}

.daemon-panel__header h2 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0;
}

.daemon-panel__header p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}

.daemon-panel__body {
  display: grid;
  gap: 18px;
  padding: 22px !important;
}

.daemon-alert {
  margin: 0 !important;
  border-radius: 8px !important;
}

.daemon-alert__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.daemon-field-block {
  display: grid;
  gap: 9px;
}

.daemon-section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
}

.daemon-section-label small {
  margin-left: auto;
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
}

.daemon-input :deep(.v-input__slot) {
  min-height: 52px !important;
  border-radius: 8px !important;
}

.daemon-input :deep(.v-input__slot fieldset) {
  border-color: #cbd5e1 !important;
}

.daemon-input :deep(.v-label--active) {
  padding: 0 4px;
  background: #fff;
}

.daemon-input :deep(.v-text-field--outlined fieldset legend) {
  width: 0 !important;
  padding: 0 !important;
}

.daemon-state {
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 12px;
  align-items: start;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 8px;
}

.daemon-state--success {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.daemon-state--warning {
  background: #fffbeb;
  border-color: #fde68a;
}

.daemon-state__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: #dcfce7;
}

.daemon-state__icon--muted {
  background: #e2e8f0;
}

.daemon-state__icon--warning {
  background: #fef3c7;
}

.daemon-state__icon--pulse {
  animation: daemon-pulse 1.6s ease-out 1;
}

.daemon-state__hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 6px 10px;
  background: #dcfce7;
  border-radius: 999px;
  color: #166534;
  font-size: 12px;
  font-weight: 700;
}

@keyframes daemon-pulse {
  0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.45); }
  100% { box-shadow: 0 0 0 14px rgba(22, 163, 74, 0); }
}

.daemon-fade-enter-active,
.daemon-fade-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}

.daemon-fade-enter,
.daemon-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.daemon-state h3 {
  margin: 0;
  color: #0f172a;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
}

.daemon-state p {
  margin: 4px 0 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
}

.daemon-link-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.daemon-link-hint .v-icon {
  flex: 0 0 auto;
  margin-top: 1px;
}

.daemon-endpoint {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 8px;
  transition: border-color 160ms ease, background 160ms ease;
}

.daemon-endpoint:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.daemon-endpoint div {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 3px;
}

.daemon-endpoint span {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.daemon-endpoint code {
  overflow-wrap: anywhere;
  color: #0f172a;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 13px;
}

.daemon-cta {
  height: 48px !important;
  border-radius: 6px !important;
  background: #1d4ed8 !important;
  box-shadow: 0 12px 24px rgba(29, 78, 216, 0.22) !important;
  color: #fff !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}

.daemon-cta:hover:not(.v-btn--disabled) {
  background: #1e40af !important;
}

.daemon-cta.v-btn--disabled {
  background: #cbd5e1 !important;
  box-shadow: none !important;
  color: #fff !important;
}

@media (max-width: 780px) {
  .daemon-shell {
    padding: 28px 14px 36px;
  }

  .daemon-header {
    flex-direction: column;
  }

  .daemon-layout {
    grid-template-columns: 1fr;
  }

  .daemon-rail {
    position: static;
  }
}

@media (max-width: 520px) {
  .daemon-brand {
    height: 26px;
    margin-bottom: 10px;
  }

  .daemon-header h1 {
    font-size: 24px;
  }

  .daemon-account {
    width: 100%;
    max-width: none;
  }

  .daemon-panel__header,
  .daemon-panel__body {
    padding: 18px !important;
  }

  .daemon-section-label {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .daemon-section-label small {
    width: 100%;
    margin-left: 25px;
  }

  .daemon-state {
    grid-template-columns: 1fr;
  }
}
</style>
